import merge from 'deepmerge';
import { green, grey, indigo, red } from '@mui/material/colors';
import { THEMES } from '../constants';

// Vibrant orange/coral for primary actions (inspired by Koojaa - highly saturated)
const customOrange = {
  50: '#fff7ed',
  100: '#ffedd5',
  200: '#fed7aa',
  300: '#fdba74',
  400: '#fb923c',
  500: '#ff8c00', // Pure saturated orange
  600: '#ff7700', // Maximum vibrancy - primary action color
  700: '#ff6600', // Deep saturated
  800: '#ff5500', // Intense orange
  900: '#ff4400', // Maximum saturation
};

// Keep blue as secondary
const customBlue = {
  50: '#e9f0fb',
  100: '#c8daf4',
  200: '#a3c1ed',
  300: '#7ea8e5',
  400: '#6395e0',
  500: '#4782da',
  600: '#407ad6',
  700: '#376fd0',
  800: '#2f65cb',
  900: '#2052c2 ',
};

const defaultVariant = {
  name: THEMES.DEFAULT,
  palette: {
    mode: 'light' as const,
    primary: {
      main: customOrange[600], // Vibrant orange for primary actions (matches Koojaa)
      contrastText: '#FFF',
    },
    secondary: {
      main: customBlue[600], // Blue for secondary elements
      contrastText: '#FFF',
    },
    background: {
      default: '#F7F9FC',
      paper: '#FFF',
    },
  },
  header: {
    color: grey[500],
    background: '#FFF',
    search: {
      color: grey[800],
    },
    indicator: {
      background: customOrange[600], // Orange indicator
    },
  },
  footer: {
    color: grey[500],
    background: '#FFF',
  },
  sidebar: {
    color: grey[200],
    background: '#233044',
    header: {
      color: grey[200],
      background: '#233044',
      brand: {
        color: customOrange[500], // Orange brand color
      },
    },
    footer: {
      color: grey[200],
      background: '#1E2A38',
      online: {
        background: green[500],
      },
    },
    badge: {
      color: '#FFF',
      background: customOrange[600], // Orange badges
    },
  },
};

const darkVariant = merge(defaultVariant, {
  name: THEMES.DARK,
  palette: {
    mode: 'dark' as const,
    primary: {
      main: customOrange[600], // Vibrant orange for primary actions
      contrastText: '#FFF',
    },
    secondary: {
      main: customBlue[500], // Keep blue as secondary
      contrastText: '#FFF',
    },
    background: {
      default: 'transparent', // Changed from "#1B2635" to allow gradient background
      paper: 'rgba(35, 48, 68, 0.8)', // Semi-transparent with blur effect
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)', // Improved from 0.5 to 0.7 for WCAG AA compliance (4.5:1 contrast ratio)
    },
  },
  header: {
    color: grey[300],
    background: 'rgba(5, 8, 15, 0.95)', // Darker than sidebar, matches app bar
    search: {
      color: grey[200],
    },
  },
  footer: {
    color: grey[300],
    background: '#233044',
  },
  sidebar: {
    color: grey[200],
    background: 'rgba(15, 20, 30, 0.85)', // Lighter than header/app bar
    header: {
      color: grey[200],
      background: 'rgba(5, 8, 15, 0.95)', // Match app bar - darker than sidebar
      brand: {
        color: customOrange[500],
      },
    },
    footer: {
      color: grey[200],
      background: 'rgba(15, 20, 30, 0.85)', // Match sidebar background
      online: {
        background: green[500],
      },
    },
    badge: {
      color: '#FFF',
      background: customOrange[600],
    },
  },
});

const lightVariant = merge(defaultVariant, {
  name: THEMES.LIGHT,
  palette: {
    mode: 'light' as const,
  },
  header: {
    color: grey[200],
    background: customBlue[800],
    search: {
      color: grey[100],
    },
    indicator: {
      background: red[700],
    },
  },
  sidebar: {
    color: grey[900],
    background: '#FFF',
    header: {
      color: '#FFF',
      background: customBlue[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: grey[800],
      background: '#F7F7F7',
      online: {
        background: green[500],
      },
    },
  },
});

const blueOverrides = {
  name: THEMES.BLUE,
  palette: {
    mode: 'light' as const,
  },
  sidebar: {
    color: '#FFF',
    background: customBlue[700],
    header: {
      color: '#FFF',
      background: customBlue[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: '#FFF',
      background: customBlue[800],
      online: {
        background: '#FFF',
      },
    },
    badge: {
      color: '#000',
      background: '#FFF',
    },
  },
};
// Explicit type args: TS 7 otherwise infers deepmerge's single-type overload from the
// override alone and drops header/footer.
const blueVariant = merge<typeof defaultVariant, typeof blueOverrides>(
  defaultVariant,
  blueOverrides,
);

const greenVariant = merge(defaultVariant, {
  name: THEMES.GREEN,
  palette: {
    mode: 'light' as const,
    primary: {
      main: green[800],
      contrastText: '#FFF',
    },
    secondary: {
      main: green[500],
      contrastText: '#FFF',
    },
  },
  header: {
    indicator: {
      background: green[600],
    },
  },
  sidebar: {
    color: '#FFF',
    background: green[700],
    header: {
      color: '#FFF',
      background: green[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: '#FFF',
      background: green[800],
      online: {
        background: '#FFF',
      },
    },
    badge: {
      color: '#000',
      background: '#FFF',
    },
  },
});

const indigoVariant = merge(defaultVariant, {
  name: THEMES.INDIGO,
  palette: {
    mode: 'light' as const,
    primary: {
      main: indigo[600],
      contrastText: '#FFF',
    },
    secondary: {
      main: indigo[400],
      contrastText: '#FFF',
    },
  },
  header: {
    indicator: {
      background: indigo[600],
    },
  },
  sidebar: {
    color: '#FFF',
    background: indigo[700],
    header: {
      color: '#FFF',
      background: indigo[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: '#FFF',
      background: indigo[800],
      online: {
        background: '#FFF',
      },
    },
    badge: {
      color: '#000',
      background: '#FFF',
    },
  },
});

const variants: Array<VariantType> = [
  defaultVariant,
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant,
];

export default variants;

export type VariantType = {
  name: string;
  palette: {
    primary: MainContrastTextType;
    secondary: MainContrastTextType;
  };
  header: ColorBgType & {
    search: {
      color: string;
    };
    indicator: {
      background: string;
    };
  };
  footer: ColorBgType;
  sidebar: ColorBgType & {
    header: ColorBgType & {
      brand: {
        color: string;
      };
    };
    footer: ColorBgType & {
      online: {
        background: string;
      };
    };
    badge: ColorBgType;
  };
};

type MainContrastTextType = {
  main: string;
  contrastText: string;
};
type ColorBgType = {
  color: string;
  background: string;
};
